import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import apiClient, { organizersAPI, locationsAPI } from '../services/api';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  TextField,
  IconButton,
  Button,
  Divider,
  CircularProgress,
  Chip,
  Badge,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Send as SendIcon,
  Chat as ChatIcon,
  Block as BlockIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  VisibilityOff as HideIcon,
  Visibility as ShowIcon,
  Folder as FolderIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  AttachFile as AttachFileIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';

interface ChatEvent {
  id: number;
  title: string;
  start_datetime: string;
  status: string;
  is_public: boolean;
  city: string;
  participants_count: number;
  unread_count: number;
  last_message_at: string | null;
  organizer_id: number;
  chat_active: boolean;
}

interface ChatMessage {
  id: number;
  user_id: number;
  message: string;
  created_at: string;
  first_name: string | null;
  last_name: string | null;
  username: string | null;
  is_organizer: boolean;
  display_name: string;
  file_url: string | null;
  file_name: string | null;
  file_size: number | null;
  file_type: string | null;
}

interface ChatEventsResponse extends ChatEvent {}

interface MessagesResponse {
  messages: ChatMessage[];
  chat_active: boolean;
  total: number;
}

const ChatsPage: React.FC = () => {
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  const { isAuthenticated } = useAuthStore();

  const [chatEvents, setChatEvents] = useState<ChatEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [chatActive, setChatActive] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const [chatFilterTypeId, setChatFilterTypeId] = useState<number | undefined>(undefined);
  const [chatFilterOrganizer, setChatFilterOrganizer] = useState('');
  const [selectedChatOrganizer, setSelectedChatOrganizer] = useState<any>(null);
  const [eventTypes, setEventTypes] = useState<any[]>([]);
  const [organizerResults, setOrganizerResults] = useState<any[]>([]);

  const [folders, setFolders] = useState<any[]>([]);
  const [chatColors, setChatColors] = useState<Record<number, string>>({});
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameFolderId, setRenameFolderId] = useState<number | null>(null);
  const [renameFolderName, setRenameFolderName] = useState('');
  const [contextMenu, setContextMenu] = useState<{ anchorEl: HTMLElement; eventId: number } | null>(null);
  const [folderMenu, setFolderMenu] = useState<{ anchorEl: HTMLElement; folderId: number } | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const shouldAutoScrollRef = useRef(true); 
  const lastMessageIdRef = useRef<number | null>(null);  

  const folderedEventIds = useMemo(() => {
    const ids = new Set<number>();
    folders.forEach((folder: any) => {
      folder.events?.forEach((ev: any) => ids.add(ev.id));
    });
    return ids;
  }, [folders]);

  const ungroupedChatEvents = useMemo(() => {
    return chatEvents.filter((event) => !folderedEventIds.has(event.id));
  }, [chatEvents, folderedEventIds]);

  const closedChatEvents = useMemo(() => {
    return chatEvents.filter((event) => !event.chat_active);
  }, [chatEvents]);

  const activeUngroupedChatEvents = useMemo(() => {
    return ungroupedChatEvents.filter((event) => event.chat_active);
  }, [ungroupedChatEvents]);

  // Загрузка списка чатов
  const loadChatEvents = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (chatFilterTypeId) params.append('type_id', String(chatFilterTypeId));
      if (chatFilterOrganizer) params.append('organizer_search', chatFilterOrganizer);
      const data = await apiClient.get<ChatEventsResponse[]>(`/api/chats/my?${params}`);
      setChatEvents(data.data);
    } catch (error) {
      console.error('Ошибка загрузки списка чатов:', error);
    } finally {
      setLoadingEvents(false);
    }
  }, [chatFilterTypeId, chatFilterOrganizer]);

  
  // Загрузка сообщений чата
  const loadMessages = useCallback(async () => {
    if (!eventId) return;
    try {
      const { data } = await apiClient.get<MessagesResponse>(`/api/chats/${eventId}/messages`);
      setMessages(data.messages);
      setChatActive(data.chat_active);
      
      if (data.messages.length > 0) {
        const lastMsgId = data.messages[data.messages.length - 1].id;
        await apiClient.post(`/api/chats/${eventId}/read`, { last_message_id: lastMsgId });
        setChatEvents(prev => prev.map(ev => ev.id === Number(eventId) ? { ...ev, unread_count: 0 } : ev));
        setFolders(prev => prev.map((folder: any) => {
          const eventIdx = folder.events?.findIndex((ev: any) => ev.id === Number(eventId));
          if (eventIdx === -1 || eventIdx === undefined) return folder;
          const updatedEvents = folder.events.map((ev: any) =>
            ev.id === Number(eventId) ? { ...ev, unread_count: 0 } : ev
          );
          const newUnreadCount = updatedEvents.filter((ev: any) => ev.unread_count > 0).length;
          return { ...folder, events: updatedEvents, unread_count: newUnreadCount };
        }));
        window.dispatchEvent(new CustomEvent('chat-marked-as-read'));
      }
    } catch (error: any) {
      console.error('Ошибка загрузки сообщений:', error);
    } finally {
      setLoadingMessages(false);
    }
  }, [eventId]);

  useEffect(() => {
    loadChatEvents();
  }, [loadChatEvents]);

  useEffect(() => {
    locationsAPI.getEventTypes().then(setEventTypes).catch(() => setEventTypes([]));
    loadFolders();
    loadChatColors();
  }, []);

  const loadFolders = async () => {
    try {
      const { data } = await apiClient.get('/api/chats/folders');
      setFolders(data);
    } catch { /* ignore */ }
  };

  const loadChatColors = async () => {
    try {
      const { data } = await apiClient.get('/api/chats/colors');
      setChatColors(data);
    } catch { /* ignore */ }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await apiClient.post('/api/chats/folders', { name: newFolderName.trim() });
      setNewFolderName('');
      setFolderDialogOpen(false);
      loadFolders();
    } catch { /* ignore */ }
  };

  const handleRenameFolder = async () => {
    if (!renameFolderId || !renameFolderName.trim()) return;
    try {
      await apiClient.put(`/api/chats/folders/${renameFolderId}`, { name: renameFolderName.trim() });
      setRenameFolderId(null);
      setRenameFolderName('');
      loadFolders();
    } catch { /* ignore */ }
  };

  const handleDeleteFolder = async (folderId: number) => {
    try {
      await apiClient.delete(`/api/chats/folders/${folderId}`);
      loadFolders();
    } catch { /* ignore */ }
  };

  const handleAddToFolder = async (folderId: number, eventId: number) => {
    try {
      await apiClient.post(`/api/chats/folders/${folderId}/add`, { event_id: eventId });
      loadFolders();
    } catch { /* ignore */ }
  };

  const handleRemoveFromFolder = async (folderId: number, eventId: number) => {
    try {
      await apiClient.delete(`/api/chats/folders/${folderId}/remove/${eventId}`);
      loadFolders();
    } catch { /* ignore */ }
  };

  const handleHideChat = async (eventId: number) => {
    try {
      await apiClient.post('/api/chats/hide', { event_id: eventId });
      loadFolders();
    } catch { /* ignore */ }
  };

  const handleUnhideChat = async (eventId: number) => {
    try {
      await apiClient.post('/api/chats/unhide', { event_id: eventId });
      loadFolders();
    } catch { /* ignore */ }
  };

  const handleSetColor = async (eventId: number, color: string | null) => {
    try {
      await apiClient.put(`/api/chats/${eventId}/color`, { color });
      setChatColors(prev => {
        const next = { ...prev };
        if (color) next[eventId] = color;
        else delete next[eventId];
        return next;
      });
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (eventId) {
      setLoadingMessages(true);
      shouldAutoScrollRef.current = true; // Сброс: при открытии чата — скроллим вниз
      loadMessages();
      
      lastMessageIdRef.current = null; // Сброс истории нового чата

      // Polling каждые 5 секунд
      if (pollingRef.current) clearInterval(pollingRef.current);
      pollingRef.current = setInterval(loadMessages, 5000);

      return () => {
        if (pollingRef.current) clearInterval(pollingRef.current);
      };
    }
  }, [eventId, loadMessages]);

  // Автоскролл вниз при новых сообщениях
//  useEffect(() => {
//    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
//  }, [messages]);
  // Автоскролл вниз при новых сообщениях (только если пользователь внизу)
  // Реагируем только на реальные новые сообщения
  useEffect(() => {
    if (messages.length === 0) {
      lastMessageIdRef.current = null;
      return;
    }

    // Последний ID в списке (сообщения идут от старых к новым)
    const currentLastId = messages[messages.length - 1].id;
    const isNewMessage = lastMessageIdRef.current === null || currentLastId > lastMessageIdRef.current;

    if (isNewMessage) {
      if (shouldAutoScrollRef.current) {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      } else {
        setUnreadCount(prev => prev + 1);
      }
      // Запоминаем ID последнего сообщения, чтобы не считать повторы
      lastMessageIdRef.current = currentLastId;
    }
  }, [messages]);

  // Отправка сообщения
  const handleSendMessage = async () => {
    if ((!eventId && !selectedFile) || (!newMessage.trim() && !selectedFile) || sending) return;

    try {
      setSending(true);
      let fileData: any = {};

      if (selectedFile && eventId) {
        setUploading(true);
        const { data: presign } = await apiClient.post('/api/uploads/presign', {
          file_name: selectedFile.name,
          file_type: selectedFile.type,
          event_id: Number(eventId),
        });

        await fetch(presign.upload_url, {
          method: 'PUT',
          body: selectedFile,
          headers: { 'Content-Type': selectedFile.type },
        });

        fileData = {
          file_url: presign.file_url,
          file_name: selectedFile.name,
          file_size: selectedFile.size,
          file_type: selectedFile.type,
        };
        setUploading(false);
      }

      await apiClient.post(`/api/chats/${eventId}/messages`, {
        message: newMessage.trim(),
        ...fileData,
      });
      setNewMessage('');
      setSelectedFile(null);
      await loadMessages();
    } catch (err: any) {
      console.error('Ошибка отправки:', err);
      alert(err.response?.data?.detail || 'Ошибка при отправке сообщения');
    } finally {
      setSending(false);
      setUploading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatMessageTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  };

  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    
    const { scrollTop, scrollHeight, clientHeight } = container;
    // Если пользователь в пределах 100px от низа — считаем, что он "внизу"
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;
    shouldAutoScrollRef.current = isAtBottom;
    
    if (isAtBottom) {
      setUnreadCount(0); // Сбрасываем счётчик, когда пользователь внизу
    }
    shouldAutoScrollRef.current = isAtBottom;
  }, []);

  // Если eventId передан в URL — открываем чат
  const selectedEvent = chatEvents.find(e => e.id === Number(eventId));

  // Мобильная адаптация: если на мобильном и выбран чат — показываем только чат
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const showList = !isMobile || !eventId;
  const showChat = !isMobile || !!eventId;

  if (!isAuthenticated) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Для доступа к чатам необходимо авторизоваться
        </Typography>
        <Button variant="contained" onClick={() => navigate('/login')} sx={{ mt: 2 }}>
          Войти
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        fontWeight="bold"
        onClick={() => navigate('/chats')}
        sx={{
          mb: 3,
          fontSize: { xs: '1rem', sm: '1.5rem' },
          cursor: 'pointer',
          '&:hover': { opacity: 0.8 },
        }}
      >
        💬 Чаты мероприятий
      </Typography>

      <Paper
        sx={{
          display: 'flex',
          height: { xs: 'calc(100vh - 200px)', sm: 'calc(100vh - 180px)' },
          maxHeight: '700px',
          overflow: 'hidden',
        }}
      >
        {/* Список мероприятий */}
        {showList && (
          <Box
            sx={{
              width: { xs: '100%', sm: 320 },
              borderRight: { sm: 1 },
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
            }}
          >
            {/* Фильтры чатов */}
            <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Autocomplete
                size="small"
                options={eventTypes}
                getOptionLabel={(option) => `${option.icon_emoji || '🎯'} ${option.name}`}
                renderInput={(params) => (
                  <TextField {...params} label="Вид мероприятия" size="small" placeholder="Все" />
                )}
                value={eventTypes.find((t) => t.id === chatFilterTypeId) || null}
                onChange={(_, newValue) => setChatFilterTypeId(newValue?.id || undefined)}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                noOptionsText="Не найдено"
                sx={{ mb: 1 }}
              />
              <Autocomplete
                size="small"
                freeSolo
                options={organizerResults}
                filterOptions={(options) => options}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return `${option.first_name || ''} ${option.last_name || ''}`.trim() || option.username || '';
                }}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  const label = `${option.first_name || ''} ${option.last_name || ''}`.trim() || option.username || '';
                  const sub = option.display_login ? `@${option.display_login}` : option.user_code ? `#${option.user_code}` : '';
                  return (
                    <Box component="li" key={key} {...otherProps}>
                      <Typography variant="body2">{label}</Typography>
                      {sub && <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>{sub}</Typography>}
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Организатор" size="small" placeholder="Имя, #код или @логин" />
                )}
                onInputChange={async (_, value, reason) => {
                  if (reason === 'reset' && !value) {
                    setOrganizerResults([]);
                    setChatFilterOrganizer('');
                    setSelectedChatOrganizer(null);
                    return;
                  }
                  if (value.length >= 2) {
                    try {
                      const data = await organizersAPI.search(value);
                      setOrganizerResults(data.users || []);
                      if (data.users && data.users.length === 1) {
                        const user = data.users[0];
                        setSelectedChatOrganizer(user);
                        const searchStr = user.user_code ? `#${user.user_code}` : user.display_login ? `@${user.display_login}` : `${user.first_name || ''} ${user.last_name || ''}`.trim();
                        setChatFilterOrganizer(searchStr);
                      }
                    } catch {
                      setOrganizerResults([]);
                    }
                  } else {
                    setOrganizerResults([]);
                  }
                }}
                onChange={(_, newValue) => {
                  if (newValue && typeof newValue !== 'string') {
                    setSelectedChatOrganizer(newValue);
                    const searchStr = newValue.user_code ? `#${newValue.user_code}` : newValue.display_login ? `@${newValue.display_login}` : `${newValue.first_name || ''} ${newValue.last_name || ''}`.trim();
                    setChatFilterOrganizer(searchStr);
                  } else if (!newValue) {
                    setSelectedChatOrganizer(null);
                    setChatFilterOrganizer('');
                  }
                }}
                value={selectedChatOrganizer}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                noOptionsText="Не найдено"
              />
            </Box>
            {loadingEvents ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : chatEvents.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <ChatIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  У вас пока нет чатов
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {/* Закрытые чаты */}
                {closedChatEvents.length > 0 && (
                  <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ px: 2, minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        <BlockIcon fontSize="small" color="action" />
                        <Typography variant="body2" fontWeight="bold">Закрытые чаты</Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontWeight: 400,
                          }}
                        >
                          {closedChatEvents.length}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <List dense>
                        {closedChatEvents.map((ev: any) => (
                          <ListItem
                            key={ev.id}
                            button
                            selected={ev.id === Number(eventId)}
                            onClick={() => navigate(`/chats/${ev.id}`)}
                            onContextMenu={(e) => { e.preventDefault(); setContextMenu({ anchorEl: e.currentTarget as any, eventId: ev.id }); }}
                            sx={{
                              opacity: 0.5,
                              pl: 4,
                              py: 2,
                              px: 2,
                              borderLeft: chatColors[ev.id] ? `4px solid ${chatColors[ev.id]}` : '4px solid transparent',
                            }}
                          >
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: 'grey.500' }}>
                                <ChatIcon />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" fontWeight="bold" noWrap sx={{ maxWidth: '70%' }}>
                                    {ev.title}
                                  </Typography>
                                  <Chip label="Неактивен" size="small" color="default" />
                                </Box>
                              }
                              secondary={
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {formatDate(ev.start_datetime)} • {ev.participants_count} уч.
                                </Typography>
                              }
                            />
                            {ev.unread_count > 0 && (
                              <Badge badgeContent={ev.unread_count} color="primary" />
                            )}
                          </ListItem>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                )}

                {closedChatEvents.length > 0 && folders.filter((f: any) => !f.is_hidden).length > 0 && <Divider />}

                {/* Папки */}
                {folders.filter((f: any) => !f.is_hidden).map((folder: any) => (
                  <Accordion key={folder.id} disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ px: 2, minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        <FolderIcon fontSize="small" color="action" />
                        <Typography variant="body2" fontWeight="bold">{folder.name}</Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: folder.unread_count > 0 ? 'primary.main' : 'text.secondary',
                            fontWeight: folder.unread_count > 0 ? 600 : 400,
                          }}
                        >
                          {folder.unread_count}/{folder.total_count}
                        </Typography>
                      </Box>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setFolderMenu({ anchorEl: e.currentTarget, folderId: folder.id }); }}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <List dense>
                        {folder.events?.map((ev: any) => (
                          <ListItem
                            key={ev.id}
                            button
                            selected={ev.id === Number(eventId)}
                            onClick={() => navigate(`/chats/${ev.id}`)}
                            onContextMenu={(e) => { e.preventDefault(); setContextMenu({ anchorEl: e.currentTarget as any, eventId: ev.id }); }}
                            sx={{
                              opacity: ev.chat_active ? 1 : 0.5,
                              pl: 4,
                              py: 2,
                              px: 2,
                              borderLeft: chatColors[ev.id] ? `4px solid ${chatColors[ev.id]}` : '4px solid transparent',
                            }}
                          >
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: ev.chat_active ? 'primary.main' : 'grey.500' }}>
                                <ChatIcon />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" fontWeight="bold" noWrap sx={{ maxWidth: '70%' }}>
                                    {ev.title}
                                  </Typography>
                                  {!ev.chat_active && (
                                    <Chip label="Неактивен" size="small" color="default" />
                                  )}
                                </Box>
                              }
                              secondary={
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {formatDate(ev.start_datetime)} • {ev.participants_count} уч.
                                </Typography>
                              }
                            />
                            {ev.unread_count > 0 && (
                              <Badge badgeContent={ev.unread_count} color="primary" />
                            )}
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRemoveFromFolder(folder.id, ev.id); }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItem>
                        ))}
                        {(!folder.events || folder.events.length === 0) && (
                          <Typography variant="caption" color="text.secondary" sx={{ px: 4, py: 1, display: 'block' }}>
                            Пусто
                          </Typography>
                        )}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                ))}

                {folders.filter((f: any) => !f.is_hidden).length > 0 && <Divider />}

                {/* Скрытые чаты */}
                {folders.filter((f: any) => f.is_hidden).map((folder: any) => (
                  <Accordion key={folder.id} disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ px: 2, minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <HideIcon fontSize="small" color="action" />
                        <Typography variant="body2" fontWeight="bold">Скрытые</Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <List dense>
                        {folder.events?.map((ev: any) => (
                          <ListItem
                            key={ev.id}
                            button
                            selected={ev.id === Number(eventId)}
                            onClick={() => navigate(`/chats/${ev.id}`)}
                            sx={{
                              opacity: ev.chat_active ? 1 : 0.5,
                              pl: 4,
                              py: 2,
                              px: 2,
                              borderLeft: chatColors[ev.id] ? `4px solid ${chatColors[ev.id]}` : '4px solid transparent',
                            }}
                          >
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: ev.chat_active ? 'primary.main' : 'grey.500' }}>
                                <ChatIcon />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" fontWeight="bold" noWrap sx={{ maxWidth: '70%' }}>
                                    {ev.title}
                                  </Typography>
                                  {!ev.chat_active && (
                                    <Chip label="Неактивен" size="small" color="default" />
                                  )}
                                </Box>
                              }
                              secondary={
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {formatDate(ev.start_datetime)} • {ev.participants_count} уч.
                                </Typography>
                              }
                            />
                            {ev.unread_count > 0 && (
                              <Badge badgeContent={ev.unread_count} color="primary" />
                            )}
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleUnhideChat(ev.id); }} title="Показать чат">
                              <ShowIcon fontSize="small" />
                            </IconButton>
                          </ListItem>
                        ))}
                        {(!folder.events || folder.events.length === 0) && (
                          <Typography variant="caption" color="text.secondary" sx={{ px: 4, py: 1, display: 'block' }}>
                            Нет скрытых чатов
                          </Typography>
                        )}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                ))}

                {/* Кнопка создания папки */}
                <Box sx={{ px: 2, py: 1 }}>
                  <Button size="small" startIcon={<AddIcon />} onClick={() => setFolderDialogOpen(true)} sx={{ textTransform: 'none' }}>
                    Создать папку
                  </Button>
                </Box>

                <Divider />

                {activeUngroupedChatEvents.map((event, index) => (
                  <React.Fragment key={event.id}>
                    <ListItem
                      button
                      selected={event.id === Number(eventId)}
                      onClick={() => {
                        if (isMobile) {
                          navigate(`/chats/${event.id}`);
                        } else {
                          navigate(`/chats/${event.id}`);
                        }
                      }}
                      onContextMenu={(e) => { e.preventDefault(); setContextMenu({ anchorEl: e.currentTarget as any, eventId: event.id }); }}
                      sx={{
                        opacity: event.chat_active ? 1 : 0.5,
                        py: 2,
                        px: 2,
                        borderLeft: chatColors[event.id] ? `4px solid ${chatColors[event.id]}` : '4px solid transparent',
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: event.chat_active ? 'primary.main' : 'grey.500' }}>
                          <ChatIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              noWrap
                              sx={{ maxWidth: '70%' }}
                            >
                              {event.title}
                            </Typography>
                            {!event.chat_active && (
                              <Chip label="Неактивен" size="small" color="default" />
                            )}
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {formatDate(event.start_datetime)} • {event.participants_count} уч.
                          </Typography>
                        }
                      />
                      {event.unread_count > 0 && (
                        <Badge badgeContent={event.unread_count} color="primary" />
                      )}
                    </ListItem>
                    {index < activeUngroupedChatEvents.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        )}

        {/* Чат */}
        {showChat && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
            }}
          >
            {!eventId ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', p: 4 }}>
                <ChatIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Выберите чат мероприятия
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center">
                  Выберите мероприятие из списка слева, чтобы начать общение
                </Typography>
              </Box>
            ) : (
              <>
                {/* Цветовая палитра */}
                <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, px: { xs: 1, sm: 1 }, py: { xs: 0.5, sm: 1 }, borderBottom: 1, borderColor: 'divider', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mr: { xs: 0.5, sm: 1 }, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>Цвет:</Typography>
                  {['#ff5722', '#2196f3', '#4caf50', '#ff9800', '#9c27b0', '#f44336'].map(color => (
                    <Box
                      key={color}
                      onClick={() => handleSetColor(Number(eventId), chatColors[Number(eventId)] === color ? null : color)}
                      sx={{
                        width: { xs: 16, sm: 20 }, height: { xs: 16, sm: 20 }, borderRadius: '50%',
                        bgcolor: color,
                        border: chatColors[Number(eventId)] === color ? '2px solid #000' : '1px solid #ccc',
                        cursor: 'pointer',
                      }}
                    />
                  ))}
                  {chatColors[Number(eventId)] && (
                    <Box
                      onClick={() => handleSetColor(Number(eventId), null)}
                      sx={{
                        width: { xs: 16, sm: 20 }, height: { xs: 16, sm: 20 }, borderRadius: '50%',
                        bgcolor: '#eee',
                        border: '1px solid #ccc',
                        cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: { xs: '0.5rem', sm: '0.6rem' },
                      }}
                    >
                      ✕
                    </Box>
                  )}
                </Box>

                {/* Шапка чата */}
                <Box
                  sx={{
                    px: { xs: 1, sm: 2 },
                    py: { xs: 0.75, sm: 2 },
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1, sm: 2 },
                  }}
                >
                  {eventId && (
                    <IconButton
                      onClick={() => navigate(`/events/${eventId}`)}
                      title="В карточку мероприятия"
                      size="small"
                      sx={{
                        flexShrink: 0,
                        '&:hover .event-card-arrow': {
                          transform: 'translateX(-2px)',
                        },
                      }}
                    >
                      <svg viewBox="0 0 32 32" width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="1" y="4" width="19" height="23" rx="2" fill="#F3F4F6" stroke="#D1D5DB" strokeWidth="0.75" />
                        <circle cx="18" cy="6.5" r="1.5" fill="#3B82F6" />
                        <g className="event-card-arrow" style={{ transition: 'transform 0.2s ease' }}>
                          <rect x="15" y="13.5" width="14" height="5" fill="#1E40AF" />
                          <path d="M15 9 L9.5 15.5 L15 22 Z" fill="#1E40AF" />
                        </g>
                      </svg>
                    </IconButton>
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant={isMobile ? 'body2' : 'body1'} fontWeight="bold" noWrap>
                      {selectedEvent?.title || 'Загрузка...'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      {selectedEvent && formatDate(selectedEvent.start_datetime)}
                    </Typography>
                  </Box>
                  {selectedEvent && !selectedEvent.chat_active && (
                    <Chip label="Чат закрыт" size="small" color="default" icon={<BlockIcon />} />
                  )}
                </Box>

                {/* Сообщения */}
                  <Box
                    ref={messagesContainerRef}
                    onScroll={handleScroll}
                    sx={{ flex: 1, overflow: 'auto', p: 2 }}
                  >
                  {loadingMessages ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : messages.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        Пока нет сообщений. Будьте первым!
                      </Typography>
                    </Box>
                  ) : (
                    <List sx={{ py: 0 }}>
                      {messages.map((msg, index) => {
                        const prevMsg = index > 0 ? messages[index - 1] : null;
                        const isContinuation = prevMsg?.user_id === msg.user_id;

                        return (
                          <Box
                            key={msg.id}
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              mb: isContinuation ? 0.5 : 2,
                            }}
                          >
                            {!isContinuation && (
                              <Avatar
                                sx={{
                                  width: 36,
                                  height: 36,
                                  fontSize: '0.85rem',
                                  bgcolor: msg.is_organizer ? '#ff8f00' : 'primary.main',
                                  mt: 0.5,
                                  mr: 1.5,
                                  ml: 1,
                                  flexShrink: 0,
                                  border: msg.is_organizer ? '2px solid #ffb74d' : 'none',
                                }}
                              >
                                {msg.display_name.charAt(0).toUpperCase()}
                              </Avatar>
                            )}
                            {isContinuation && <Box sx={{ width: 56, flexShrink: 0 }} />}

                            <Box
                              sx={{
                                maxWidth: '75%',
                                p: 1.5,
                                borderRadius: msg.is_organizer ? '16px 16px 16px 4px' : 2,
                                bgcolor: msg.is_organizer
                                  ? '#fff8e1'
                                  : 'background.paper',
                                borderLeft: msg.is_organizer ? '4px solid #ff8f00' : 'none',
                                boxShadow: msg.is_organizer ? '0 2px 8px rgba(255, 143, 0, 0.15)' : '0 1px 3px rgba(0,0,0,0.08)',
                              }}
                            >
                              {!isContinuation && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                                  <Typography
                                    variant="caption"
                                    fontWeight={msg.is_organizer ? 'bold' : 'normal'}
                                    color={msg.is_organizer ? '#e65100' : 'text.secondary'}
                                  >
                                    {msg.display_name}
                                  </Typography>
                                  {msg.is_organizer && (
                                    <Box
                                      component="span"
                                      sx={{
                                        bgcolor: '#ff8f00',
                                        color: '#fff',
                                        fontSize: '0.65rem',
                                        px: 0.7,
                                        py: 0.2,
                                        borderRadius: 1,
                                        fontWeight: 'bold',
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.5,
                                      }}
                                    >
                                      🎯 Организатор
                                    </Box>
                                  )}
                                </Box>
                              )}
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {msg.message}
                              </Typography>
                              {msg.file_url && (
                                <Box sx={{ mt: 1 }}>
                                  {msg.file_type?.startsWith('image/') ? (
                                    <Box
                                      component="img"
                                      src={msg.file_url}
                                      alt={msg.file_name || 'Изображение'}
                                      sx={{ maxWidth: '100%', maxHeight: 200, borderRadius: 1, cursor: 'pointer' }}
                                      onClick={() => window.open(msg.file_url!, '_blank')}
                                    />
                                  ) : (
                                    <Button
                                      size="small"
                                      startIcon={<FileIcon />}
                                      href={msg.file_url}
                                      target="_blank"
                                      rel="noopener"
                                      sx={{ textTransform: 'none' }}
                                    >
                                      {msg.file_name || 'Файл'}
                                      {msg.file_size && ` (${Math.round(msg.file_size / 1024)} КБ)`}
                                    </Button>
                                  )}
                                </Box>
                              )}
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mt: 0.5, textAlign: 'right' }}
                              >
                                {formatMessageTime(msg.created_at)}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </List>
                  )}
                </Box>

                {unreadCount > 0 && !shouldAutoScrollRef.current && (
                  <Box sx={{ px: 2, pb: 1, textAlign: 'center' }}>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                        setUnreadCount(0);
                      }}
                      sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                      🔽 Новые: {unreadCount}
                    </Button>
                  </Box>
                )}

                {/* Поле ввода */}
                <Box
                  sx={{
                    px: { xs: 1, sm: 2 },
                    py: { xs: 1, sm: 2 },
                    borderTop: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    gap: { xs: 0.5, sm: 1 },
                    bgcolor: 'background.paper',
                    alignItems: 'flex-end',
                  }}
                >
                  {chatActive ? (
                    <>
                      <IconButton component="label" size="small" disabled={uploading} sx={{ mb: 0.5 }}>
                        <AttachFileIcon />
                        <input
                          type="file"
                          hidden
                          accept="image/*,.pdf,.doc,.docx"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file && file.size <= 20 * 1024 * 1024) {
                              setSelectedFile(file);
                            } else {
                              alert('Файл не должен превышать 20 МБ');
                            }
                          }}
                        />
                      </IconButton>
                      {selectedFile && (
                        <Chip
                          label={selectedFile.name}
                          onDelete={() => setSelectedFile(null)}
                          size="small"
                          icon={<FileIcon />}
                          sx={{ maxWidth: 200, mb: 0.5 }}
                        />
                      )}
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Введите сообщение..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={handleKeyDown}
                        multiline
                        minRows={isMobile ? 3 : 5}
                        maxRows={isMobile ? 7 : 10}
                        disabled={sending}
                        sx={{
                          '& textarea': {
                            maxHeight: isMobile ? '7em' : '10em',
                            overflowY: 'auto',
                          },
                        }}
                      />
                      <IconButton
                        color="primary"
                        onClick={handleSendMessage}
                        disabled={!newMessage.trim() || sending}
                        sx={{ mb: 0.5 }}
                      >
                        <SendIcon />
                      </IconButton>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1, textAlign: 'center', width: '100%' }}>
                      Чат неактивен. Общение доступно в течение 30 дней после начала мероприятия.
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </Box>
        )}
      </Paper>

      {/* Контекстное меню чата */}
      <Menu
        open={!!contextMenu}
        onClose={() => setContextMenu(null)}
        anchorEl={contextMenu?.anchorEl}
      >
        {contextMenu && folders.filter((f: any) => !f.is_hidden).map((folder: any) => (
          <MenuItem key={folder.id} onClick={() => { handleAddToFolder(folder.id, contextMenu.eventId); setContextMenu(null); }}>
            📁 В папку «{folder.name}»
          </MenuItem>
        ))}
        <MenuItem onClick={() => { handleHideChat(contextMenu!.eventId); setContextMenu(null); }}>
          <HideIcon fontSize="small" sx={{ mr: 1 }} /> Скрыть чат
        </MenuItem>
      </Menu>

      {/* Меню папки */}
      <Menu
        open={!!folderMenu}
        onClose={() => setFolderMenu(null)}
        anchorEl={folderMenu?.anchorEl}
      >
        <MenuItem onClick={() => {
          const folder = folders.find((f: any) => f.id === folderMenu?.folderId);
          if (folder) { setRenameFolderId(folder.id); setRenameFolderName(folder.name); setFolderDialogOpen(false); }
          setFolderMenu(null);
        }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Переименовать
        </MenuItem>
        <MenuItem onClick={() => { handleDeleteFolder(folderMenu!.folderId); setFolderMenu(null); }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Удалить
        </MenuItem>
      </Menu>

      {/* Диалог создания/переименования папки */}
      <Dialog open={folderDialogOpen || !!renameFolderId} onClose={() => { setFolderDialogOpen(false); setRenameFolderId(null); }}>
        <DialogTitle>{renameFolderId ? 'Переименовать папку' : 'Новая папка'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Название папки"
            value={renameFolderId ? renameFolderName : newFolderName}
            onChange={(e) => renameFolderId ? setRenameFolderName(e.target.value) : setNewFolderName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setFolderDialogOpen(false); setRenameFolderId(null); }}>Отмена</Button>
          <Button variant="contained" onClick={renameFolderId ? handleRenameFolder : handleCreateFolder}>Сохранить</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatsPage;
